import { useLayoutEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react";

/** Keyboard parity for small, mutually exclusive button groups. */
export function choiceKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
  if (event.nativeEvent.isComposing || event.ctrlKey || event.metaKey || event.altKey || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
  if (event.currentTarget.getAttribute("role") === "tablist" && event.currentTarget.getAttribute("aria-orientation") !== "vertical" && ["ArrowUp", "ArrowDown"].includes(event.key)) return;
  const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")].filter((button) => button.getClientRects().length > 0);
  if (!buttons.length) return;
  const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
  if (index < 0) return;
  event.preventDefault();
  const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : (index + (["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1) + buttons.length) % buttons.length;
  buttons[next]?.focus(); buttons[next]?.click();
}

/** Popover menus are not dialogs: Escape restores the trigger; Tab leaves the menu. */
export function useMenuKeyboard(open: boolean, selector: string, onClose: () => void) {
  const close = useRef(onClose); close.current = onClose;
  useLayoutEffect(() => {
    if (!open) return;
    const menu = document.querySelector<HTMLElement>(selector);
    if (!menu) return;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Transfer focus in the same commit as opening. Deferring one frame loses fast
    // follow-up keys (End/ArrowDown) while focus is still on the trigger.
    menu.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus({ preventScroll: true });
    const rove = () => menu.querySelectorAll<HTMLButtonElement>('button').forEach((button) => { button.tabIndex = button === document.activeElement ? 0 : -1; });
    rove(); menu.addEventListener('focusin', rove);
    const key = (event: KeyboardEvent) => {
      if (event.isComposing || event.ctrlKey || event.metaKey || event.altKey || !menu.contains(document.activeElement)) return;
      const buttons = [...menu.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")];
      if (event.key === "Escape") {
        event.preventDefault(); event.stopPropagation(); close.current(); trigger?.focus();
      } else if (event.key === "Tab") {
        event.preventDefault();
        const outside = [...document.querySelectorAll<HTMLElement>('button,a[href],input,select,textarea,[tabindex]')].filter((node) => node.tabIndex >= 0 && node.getClientRects().length && !node.matches(':disabled') && !node.closest('[inert],[aria-hidden="true"],[hidden]') && getComputedStyle(node).visibility !== 'hidden' && !menu.contains(node));
        const index = trigger ? outside.indexOf(trigger) : -1;
        const next = outside[index + (event.shiftKey ? -1 : 1)] ?? trigger;
        close.current(); requestAnimationFrame(() => { if (!document.querySelector('[role="dialog"]')) next?.focus(); });
      } else if (buttons.length && ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
        event.preventDefault(); event.stopPropagation();
        const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
        const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : (index + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length;
        buttons[next]?.focus();
      }
    };
    menu.addEventListener("keydown", key);
    return () => {
      menu.removeEventListener("keydown", key); menu.removeEventListener('focusin', rove);
      // Removing a focused menu item must not strand focus on <body>. Do not
      // steal focus from a newly opened dialog, clicked control or destination.
      queueMicrotask(() => {
        if (trigger?.isConnected && !trigger.closest('[inert]') && !document.querySelector('[role="dialog"]') && (document.activeElement === document.body || document.activeElement === null)) trigger.focus({ preventScroll: true });
      });
    };
  }, [open, selector]);
}
